// Email API — typed-обёртки над endpoints почты (V15 «Почта» W7, §3.5; канон payments.ts).
// Контракт (backend EmailAccountDto/EmailTemplateDto/EmailDeliveryDto/EmailServiceException §3.5):
//   - GET    /api/email/settings                → {accounts, activeAccountId, googleOAuthEnabled?} (секретов нет — hasSecret);
//   - PUT    /api/email/settings/smtp           → EmailAccountDto (password null/пустой — прежний);
//   - DELETE /api/email/settings/smtp           → 204;
//   - DELETE /api/email/settings/gmail          → 204 (нет GMAIL → 404, уже отключён);
//   - PUT    /api/email/settings/activate       → EmailAccountDto {accountId} (единственный активный);
//   - GET    /api/email/oauth/google/authorize  → {authorizeUrl} (window.location = authorizeUrl);
//   - POST   /api/email/oauth/google/refresh    → EmailAccountDto (200) / 502 EMAIL_OAUTH_EXCHANGE_FAILED;
//   - GET    /api/email/templates?page=&size=   → Page<EmailTemplate> (свои + системные, is_system DESC);
//   - POST   /api/email/templates               → 201 EmailTemplateDto (dup name → 409);
//   - PUT    /api/email/templates/{id}          → EmailTemplateDto (системный/чужой → 403);
//   - DELETE /api/email/templates/{id}          → 204;
//   - POST   /api/email/templates/preview       → EmailTemplatePreviewDto{subject, body, recipientEmail};
//   - GET    /api/email/deliveries?page=&size=  → Page<EmailDelivery> (RBAC: свои / admin — все).

import { apiClient } from './client';
import type { Page } from './tasks';

/** Провайдер почты (backend EmailProvider; значения — wire-код toDb()). */
export type EmailProvider = 'SMTP' | 'GMAIL';

/** Протокол безопасности SMTP (backend SmtpSecurityType; PLAIN вне MVP — запрет секретов §3.4). */
export type SmtpSecurityType = 'SSL' | 'STARTTLS';

/** Тип контента письма (backend EmailContentType). */
export type EmailContentType = 'HTML' | 'PLAIN';

/** Исход попытки отправки (backend EmailDeliveryStatus; V41 CHECK 5 значений). */
export type EmailDeliveryStatus =
  | 'SENT'
  | 'FAILED'
  | 'SKIPPED_NO_ACCOUNT'
  | 'SKIPPED_NO_RECIPIENT'
  | 'SKIPPED_RATE_LIMIT';

/** Аккаунт почты юзера (wire EmailAccountDto §3.5; СЕКРЕТОВ нет — только hasSecret). */
export interface EmailAccount {
  id: string;
  provider: EmailProvider;
  /** Отправитель («от лица юзера», §1: SMTP — username; GMAIL — email из userinfo). */
  fromEmail: string;
  displayName: string | null;
  /** SMTP-only поля (provider='SMTP'): nullable для GMAIL. */
  smtpHost: string | null;
  smtpPort: number | null;
  smtpSecurity: SmtpSecurityType | null;
  username: string | null;
  /** Единственный активный (partial unique idx_email_accounts_active_user). */
  isActive: boolean;
  /** Пароль приложения / refresh_token сохранён (в DTO — только флаг, не значение). */
  hasSecret: boolean;
  createdAt: string;
  updatedAt: string;
  version: number;
}

/** Ответ GET /api/email/settings ({activeAccountId} — null, если не настроен). */
export interface EmailSettings {
  accounts: EmailAccount[];
  activeAccountId: string | null;
  /** OAuth-клиент задан на сервере; отсутствует/false — SMTP-only. */
  googleOAuthEnabled?: boolean;
}

/** Тело PUT /api/email/settings/smtp (password опционален: пустой → прежний секрет, §3.5). */
export interface SmtpAccountUpsertRequest {
  fromEmail: string;
  displayName?: string;
  smtpHost: string;
  smtpPort: number;
  smtpSecurity: SmtpSecurityType;
  username: string;
  password?: string;
}

/** Шаблон письма (wire EmailTemplateDto §3.5; isSystem — read-only, CRUD только своих). */
export interface EmailTemplate {
  id: string;
  name: string;
  subject: string;
  contentType: EmailContentType;
  body: string;
  isSystem: boolean;
  createdAt: string;
  updatedAt: string;
  version: number;
}

/** Тело POST/PUT /api/email/templates (валидация M-7: name ≤120, subject ≤255, body ≤10000). */
export interface TemplateUpsertRequest {
  name: string;
  subject: string;
  contentType: EmailContentType;
  body: string;
}

/** Ответ POST /api/email/templates/preview — рендер без отправки (§3.3). */
export interface EmailTemplatePreview {
  subject: string;
  body: string;
  recipientEmail: string | null;
}

/** Запись лога отправок (wire EmailDeliveryDto §3.5; bodyPreview ≤4000 — PII-минимизация). */
export interface EmailDelivery {
  id: string;
  invoiceId: string | null;
  templateId: string | null;
  accountId: string | null;
  provider: EmailProvider;
  recipientEmail: string;
  subject: string;
  bodyPreview: string | null;
  status: EmailDeliveryStatus;
  errorMessage: string | null;
  sentAt: string | null;
  createdByUserId: string | null;
  createdAt: string;
}

/** Ответ POST /api/invoices/{id}/send-email (M-3: 202 + исход в теле, НЕ 5xx). */
export interface SendEmailResponse {
  deliveryId: string;
  status: EmailDeliveryStatus;
  error: string | null;
}

/** Ответ GET /api/email/oauth/google/authorize — consent URL для window.location. */
export interface AuthorizeResponse {
  authorizeUrl: string;
}

/** Email API. */
export const emailApi = {
  /** GET /api/email/settings → свои аккаунты + активный (admin — тоже свои, OQ-5). */
  async getSettings(): Promise<EmailSettings> {
    const { data } = await apiClient.get<EmailSettings>('/api/email/settings');
    return data;
  },

  /** PUT /api/email/settings/smtp → upsert SMTP-аккаунта (200 EmailAccountDto). */
  async upsertSmtp(payload: SmtpAccountUpsertRequest): Promise<EmailAccount> {
    const { data } = await apiClient.put<EmailAccount>('/api/email/settings/smtp', payload);
    return data;
  },

  /** DELETE /api/email/settings/smtp → 204 (нет SMTP → 404). */
  async deleteSmtp(): Promise<void> {
    await apiClient.delete('/api/email/settings/smtp');
  },

  /** DELETE /api/email/settings/gmail → 204 (нет GMAIL → 404 EMAIL_ACCOUNT_NOT_FOUND). */
  async deleteGmail(): Promise<void> {
    await apiClient.delete('/api/email/settings/gmail');
  },

  /** PUT /api/email/settings/activate {accountId} → единственный активный (403/404). */
  async activate(accountId: string): Promise<EmailAccount> {
    const { data } = await apiClient.put<EmailAccount>('/api/email/settings/activate', { accountId });
    return data;
  },

  /** GET /api/email/oauth/google/authorize → {authorizeUrl} (FE: window.location.assign). */
  async getAuthorizeUrl(): Promise<AuthorizeResponse> {
    const { data } = await apiClient.get<AuthorizeResponse>('/api/email/oauth/google/authorize');
    return data;
  },

  /** POST /api/email/oauth/google/refresh → обновлённый GMAIL-аккаунт (502 — отзыв токена). */
  async refreshGmail(): Promise<EmailAccount> {
    const { data } = await apiClient.post<EmailAccount>('/api/email/oauth/google/refresh');
    return data;
  },

  /** GET /api/email/templates → свои + системные (системные первыми, по имени). */
  async listTemplates(page = 0, size = 20): Promise<Page<EmailTemplate>> {
    const { data } = await apiClient.get<Page<EmailTemplate>>('/api/email/templates', {
      params: { page, size },
    });
    return data;
  },

  /** POST /api/email/templates → 201 EmailTemplateDto (dup name → 409). */
  async createTemplate(payload: TemplateUpsertRequest): Promise<EmailTemplate> {
    const { data } = await apiClient.post<EmailTemplate>('/api/email/templates', payload);
    return data;
  },

  /** PUT /api/email/templates/{id} → только свои (системный/чужой → 403). */
  async updateTemplate(id: string, payload: TemplateUpsertRequest): Promise<EmailTemplate> {
    const { data } = await apiClient.put<EmailTemplate>(`/api/email/templates/${id}`, payload);
    return data;
  },

  /** DELETE /api/email/templates/{id} → 204 (системный/чужой → 403). */
  async deleteTemplate(id: string): Promise<void> {
    await apiClient.delete(`/api/email/templates/${id}`);
  },

  /**
   * POST /api/email/templates/preview → рендер без отправки.
   * Ровно один контекст: invoiceId XOR dealId XOR leadId (существующий invoiceId остаётся валиден).
   */
  async previewTemplate(req: {
    templateId: string;
    invoiceId?: string;
    dealId?: string;
    leadId?: string;
  }): Promise<EmailTemplatePreview> {
    const { data } = await apiClient.post<EmailTemplatePreview>('/api/email/templates/preview', req);
    return data;
  },

  /** GET /api/email/deliveries → история отправок (свои / admin — все; created_at DESC). */
  async listDeliveries(page = 0, size = 20): Promise<Page<EmailDelivery>> {
    const { data } = await apiClient.get<Page<EmailDelivery>>('/api/email/deliveries', {
      params: { page, size },
    });
    return data;
  },
};
